/**
 * src/lib/format.ts
 *
 * Small formatting helpers — relative time, percent, byte counts.
 * Locale-agnostic and side-effect free.
 */

export function relativeTime(iso: string | undefined): string {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return '—';
  const sec = Math.round(ms / 1000);
  if (sec < 0) return 'in future';
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.round(hr / 24);
  return `${d}d ago`;
}

export function pct(value: number | undefined, digits = 1): string {
  if (value === undefined || value === null || Number.isNaN(value)) return '—';
  return `${(value * 100).toFixed(digits)}%`;
}

export function compactNumber(n: number | undefined): string {
  if (n === undefined || n === null) return '—';
  if (n < 1_000) return String(n);
  if (n < 1_000_000) return `${(n / 1_000).toFixed(1)}k`;
  return `${(n / 1_000_000).toFixed(1)}M`;
}

/**
 * A number from the database, formatted — or '—' if it is not a number.
 *
 * Checks the TYPE, not just null/undefined. On 2026-09-17 the Deals page
 * crashed with `r.dcs.toFixed is not a function` because a backend bug had
 * written `dcs: {}` onto 46k deals. The guard at that site was
 * `r.dcs === undefined ? '—' : r.dcs.toFixed(0)` — which lets `{}` straight
 * through to `.toFixed`. One malformed row took down the whole page for the
 * team, because React unmounts everything on a render error.
 *
 * A dashboard's job is to SHOW bad data, not die on it. Use this for any value
 * that comes from a stored document rather than being computed here.
 */
export function num(value: unknown, digits = 0): string {
  return typeof value === 'number' && Number.isFinite(value) ? value.toFixed(digits) : '—';
}

/**
 * A stored scalar, rendered — or '—' if it is not one.
 *
 * `num()` covers values we format as numbers. This covers the other half:
 * values we drop straight into JSX as `{r.modelKey}`. React throws error #31
 * ("Objects are not valid as a React child") on an object, and because there
 * was no error boundary, that unmounted the entire dashboard rather than
 * spoiling one cell.
 *
 * The same backend bug that produced `dcs: {}` produced `modelKey: {}` on 53k
 * rows, `category: {}` on 29k and `condition: {}` on 54k — all typed as
 * strings, so TypeScript gives no warning at these sites. The type is a claim
 * about what the writer intended, not about what is in the collection.
 */
export function txt(value: unknown): string {
  if (typeof value === 'string') return value || '—';
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : '—';
  if (typeof value === 'boolean') return String(value);
  return '—';
}
